import { Fragment, useState, useEffect } from 'react';
import { animated, useSpring } from 'react-spring';
import MiddleSchoolBackCustomProps from '../MiddleSchoolBackButton/MiddleSchoolBackCustomProps';
import {
  values,
  getInnerSlicePath,
  getInnerTextCoordinates,
  getOuterSlicePath,
  getOuterTextCoordinates,
} from '../../CheckIn/checkInValues';
import styles from './CheckInToolMobile.module.scss';

interface Props {
  handleEmotionClick: (arg0: string) => void;
  selectedEmotion: string;
}

const CheckInToolMobile = ({ handleEmotionClick, selectedEmotion }: Props): JSX.Element => {
  const [innerSelection, setInnerSelection] = useState('');
  const [origin, setOrigin] = useState([175, 175]);

  const { viewBox } = useSpring({
    from: { viewBox: `0 0 350 350` },
    to: { viewBox: `${origin[0] - 175} ${origin[1] - 175} 350 350` },
  });

  const handleClick = (emotion: string) => {
    handleEmotionClick(emotion);
  };

  const handleInnerClick = (category: string, location: [number, number]) => {
    setInnerSelection(category);
    setOrigin([location[1], location[0]]);
    handleEmotionClick('');
  };

  const resetState = () => {
    setInnerSelection('');
    setOrigin([175, 175]);
    handleEmotionClick('');
  };

  useEffect(() => {
    resetState();
  }, []);
  return (
    <div>
      <animated.svg height='350px' width='350px' viewBox={viewBox} className={styles.pieArea}>
        {values.map((value, index) => {
          const xOrigin = 175;
          const yOrigin = 175;
          const radius = 125;
          const radiansPerUnit = (2 * Math.PI) / values.length;
          const pathValue = getInnerSlicePath(xOrigin, yOrigin, index * radiansPerUnit, radiansPerUnit, radius);

          const [xTextLocation, yTextLocation] = getInnerTextCoordinates(
            xOrigin,
            yOrigin,
            index * radiansPerUnit,
            radiansPerUnit,
            radius / 1.5,
          );

          return (
            <Fragment key={value.title}>
              <g
                className={styles.innerSegment}
                style={{
                  opacity: innerSelection === value.title || innerSelection === '' ? 1 : 0.2,
                }}
                onClick={() =>
                  handleInnerClick(
                    value.title,
                    getInnerTextCoordinates(xOrigin, yOrigin, index * radiansPerUnit, radiansPerUnit, radius * 1.1),
                  )
                }
              >
                <path d={pathValue} fill={value.segmentColor} stroke='black' />
              </g>

              <text
                className={styles.innerText}
                transform={'rotate(90)'}
                x={xTextLocation}
                y={-yTextLocation}
                fontSize={'13px'}
                fill={index < 3 || index > 5 ? 'black' : 'white'}
                textAnchor='middle'
              >
                {value.title}
              </text>
              {value.subEmotions.map((emotion, subIndex) => {
                const radiansPerEmotion = radiansPerUnit / value.subEmotions.length;
                const emotionIndex = index * values.length + subIndex;
                const emotionPathValue = getOuterSlicePath(
                  xOrigin,
                  yOrigin,
                  emotionIndex * radiansPerEmotion,
                  radiansPerEmotion,
                  radius,
                );
                const [outerTextX, outerTextY] = getOuterTextCoordinates(
                  xOrigin,
                  yOrigin,
                  emotionIndex * radiansPerEmotion,
                  radiansPerEmotion,
                  radius * 1.95,
                );
                const [scaleOriginX, scaleOriginY] = getOuterTextCoordinates(
                  xOrigin,
                  yOrigin,
                  emotionIndex * radiansPerEmotion,
                  radiansPerEmotion,
                  radius,
                );
                const textAngle = (emotionIndex + 0.5) * radiansPerEmotion * 57.2958;
                return innerSelection !== value.title ? null : (
                  <Fragment key={emotion}>
                    <g
                      className={selectedEmotion === emotion ? styles.outerActive : styles.outerSegment}
                      style={{
                        transformOrigin: `${scaleOriginX}px ${scaleOriginY}px`,
                      }}
                      onClick={() => handleClick(emotion)}
                    >
                      <path d={emotionPathValue} fill={value.subEmotionColor} stroke='black' />
                    </g>

                    <text
                      className={styles.outerText}
                      transform={`rotate(${
                        emotionIndex < 24 ? textAngle : textAngle + 180
                      }, ${outerTextX}, ${outerTextY})`}
                      x={outerTextX}
                      y={outerTextY}
                      fontSize={'12px'}
                      fill='black'
                      textAnchor={emotionIndex > 23 ? 'start' : 'end'}
                      alignmentBaseline='middle'
                    >
                      {emotion}
                    </text>
                  </Fragment>
                );
              })}
            </Fragment>
          );
        })}
      </animated.svg>
      {innerSelection !== '' && (
        <div className={styles.backContainer}>
          <MiddleSchoolBackCustomProps handleClick={resetState} />
        </div>
      )}
    </div>
  );
};

export default CheckInToolMobile;
